import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  PlayCircle, Search, Tv, Loader2, AlertTriangle, ArrowLeft, Sparkles, Wifi, Radio,
} from "lucide-react";
import videojs from "video.js";
import "video.js/dist/video-js.css";

import api from "@/lib/api";
import Footer from "@/saas/components/Footer";
import { useSaasAuth } from "@/saas/context/SaasAuthContext";

/**
 * Phase 9 — LUMEN Web Player
 *
 * - Subscription gate: only customers with `customer.status === 'active'` AND
 *   an active line whose expiry is in the future. Otherwise redirect to /plans.
 * - Sources fetched from GET /saas/me/channels (server enforces the same gate).
 * - Video.js HLS player initialised once, src swapped on channel change.
 * - Cinematic UI: left rail = channel browser with search + category filter,
 *   right = player + meta. Falls back to a public demo HLS when a channel has
 *   no real stream URL configured (sandbox-friendly).
 */
export default function Player() {
  const navigate = useNavigate();
  const { user, token, loading } = useSaasAuth();

  const [data, setData] = useState({ channels: [], categories: [] });
  const [fetchErr, setFetchErr] = useState("");
  const [busy, setBusy] = useState(true);

  const [activeId, setActiveId] = useState(null);
  const [query, setQuery] = useState("");
  const [cat, setCat] = useState("all");

  const videoRef  = useRef(null);
  const playerRef = useRef(null);

  // ---- 1) Subscription gating: redirect non-eligible customers up-front ----
  const customer = user?.customer;
  const isActive = customer?.status === "active";
  const termsBlocked = !!user?.terms_status?.required;

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login", { replace: true }); return; }
    if (termsBlocked) { navigate("/dashboard", { replace: true }); return; }
    if (!isActive) { navigate("/dashboard", { replace: true }); return; }
  }, [loading, user, isActive, termsBlocked, navigate]);

  // ---- 2) Fetch channel list ----
  useEffect(() => {
    if (!token || !isActive) return;
    let alive = true;
    setBusy(true);
    api.get("/saas/me/channels", { headers: { Authorization: `Bearer ${token}` } })
       .then((r) => { if (alive) setData(r.data || { channels: [], categories: [] }); })
       .catch((e) => { if (alive) setFetchErr(e?.response?.data?.detail || "Kanal listesi alınamadı."); })
       .finally(() => { if (alive) setBusy(false); });
    return () => { alive = false; };
  }, [token, isActive]);

  // First channel auto-selected when list arrives
  useEffect(() => {
    if (!activeId && data.channels.length > 0) setActiveId(data.channels[0].id);
  }, [data, activeId]);

  // ---- 3) Filtering ----
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return data.channels.filter((c) => {
      if (cat !== "all" && c.category !== cat) return false;
      if (q && !c.name.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [data, query, cat]);

  const active = useMemo(
    () => data.channels.find((c) => c.id === activeId) || null,
    [data, activeId]
  );

  // ---- 4) Initialise / dispose video.js (recommended React pattern) ----
  useEffect(() => {
    if (playerRef.current) return;
    const containerEl = videoRef.current;
    if (!containerEl) return;
    const videoEl = document.createElement("video");
    videoEl.classList.add("video-js", "vjs-default-skin", "vjs-big-play-centered");
    videoEl.setAttribute("playsinline", "");
    videoEl.setAttribute("preload", "auto");
    containerEl.innerHTML = "";
    containerEl.appendChild(videoEl);
    const handle = requestAnimationFrame(() => {
      playerRef.current = videojs(videoEl, {
        controls: true,
        autoplay: false,
        preload: "auto",
        fluid: true,
        responsive: true,
        controlBar: { pictureInPictureToggle: true },
      });
    });
    return () => {
      cancelAnimationFrame(handle);
      const p = playerRef.current;
      if (p && !p.isDisposed()) {
        p.dispose();
      }
      playerRef.current = null;
    };
  }, []);

  // ---- 5) Swap src on active channel change ----
  useEffect(() => {
    if (!active) return;
    let attempts = 0;
    const trySetSrc = () => {
      const p = playerRef.current;
      if (!p || p.isDisposed()) {
        // Player not ready yet — retry a couple of times until init finishes
        if (attempts++ < 30) setTimeout(trySetSrc, 80);
        return;
      }
      try {
        p.src({
          src: active.stream_url,
          type: active.stream_url.endsWith(".m3u8")
            ? "application/x-mpegURL"
            : "video/mp4",
        });
        p.ready(() => {
          p.play()?.catch(() => { /* user click required */ });
        });
      } catch { /* noop */ }
    };
    trySetSrc();
  }, [active]);

  if (loading || (user && !isActive && !termsBlocked === false)) {
    return (
      <div className="min-h-screen grid place-items-center bg-bg text-white">
        <Loader2 className="w-7 h-7 animate-spin text-gold" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg text-white" data-testid="player-screen">
      {/* Slim cinematic header (player has its own immersive top bar) */}
      <header className="sticky top-0 z-30 bg-bg/85 backdrop-blur-md border-b border-white/5">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 h-14 flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate("/dashboard")}
            className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-white/60 hover:text-gold transition-colors"
            data-testid="player-back"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Panele Dön
          </button>
          <div className="ml-2 flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-gold/85">
            <Sparkles className="w-3 h-3" /> LUMEN Web Player
          </div>
          <div className="ml-auto flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-emerald-400">
            <Wifi className="w-3 h-3" /> Live
          </div>
        </div>
      </header>

      <main className="max-w-screen-2xl mx-auto px-3 sm:px-6 py-6 grid lg:grid-cols-[340px_1fr] gap-6">
        {/* ===== Left rail: Channel browser ===== */}
        <aside
          className="bg-surface/60 border border-white/10 rounded-2xl overflow-hidden flex flex-col max-h-[calc(100vh-7rem)]"
          data-testid="player-channel-rail"
        >
          {/* Search + category */}
          <div className="p-4 space-y-3 border-b border-white/5 shrink-0">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/35" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Kanal ara…"
                className="w-full bg-bg/70 border border-white/10 rounded-lg pl-10 pr-3 h-10 text-sm placeholder:text-white/30 focus:border-gold/50 outline-none transition"
                data-testid="player-search"
              />
            </div>
            {data.categories.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                <Pill active={cat === "all"} onClick={() => setCat("all")}>Tümü</Pill>
                {data.categories.map((c) => (
                  <Pill key={c} active={cat === c} onClick={() => setCat(c)}>{c}</Pill>
                ))}
              </div>
            )}
            <div className="text-[10px] font-mono uppercase tracking-widest text-white/35">
              {filtered.length} kanal
            </div>
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto py-2">
            {busy && (
              <div className="px-4 py-12 grid place-items-center text-white/40">
                <Loader2 className="w-5 h-5 animate-spin" />
              </div>
            )}
            {!busy && fetchErr && (
              <div className="m-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-xs flex items-start gap-2" data-testid="player-fetch-err">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>{fetchErr}</span>
              </div>
            )}
            {!busy && !fetchErr && filtered.length === 0 && (
              <div className="px-6 py-12 text-center text-sm text-white/45">
                <Tv className="w-8 h-8 mx-auto mb-2 text-white/20" />
                Eşleşen kanal yok.
              </div>
            )}
            {!busy && filtered.map((c) => {
              const isAct = c.id === activeId;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setActiveId(c.id)}
                  className={`w-full px-4 py-2.5 flex items-center gap-3 text-left transition group ${
                    isAct
                      ? "bg-gold/15 border-l-2 border-gold"
                      : "hover:bg-white/5 border-l-2 border-transparent"
                  }`}
                  data-testid={`player-channel-${c.id}`}
                >
                  <div className={`w-9 h-9 rounded-md grid place-items-center shrink-0 ${
                    isAct ? "bg-gold text-black" : "bg-white/5 text-white/50 group-hover:text-white"
                  }`}>
                    {c.logo_url
                      ? <img src={c.logo_url} alt="" className="w-full h-full object-contain rounded-md" onError={(e) => { e.target.style.display = "none"; }} />
                      : <Radio className="w-4 h-4" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className={`text-sm truncate font-medium ${isAct ? "text-gold" : "text-white"}`}>{c.name}</div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-white/35 truncate">{c.category}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </aside>

        {/* ===== Right: Player ===== */}
        <section className="flex flex-col gap-4 min-w-0">
          <div className="relative aspect-video rounded-2xl overflow-hidden border border-white/10 bg-black shadow-[0_30px_120px_-24px_rgba(245,166,35,0.25)]" data-testid="player-stage">
            <div ref={videoRef} data-vjs-player className="w-full h-full" />
            {!active && !busy && (
              <div className="absolute inset-0 grid place-items-center text-white/35 text-sm pointer-events-none">
                <div className="flex flex-col items-center gap-2">
                  <PlayCircle className="w-10 h-10" />
                  <span>Bir kanal seçin</span>
                </div>
              </div>
            )}
          </div>

          {/* Now playing */}
          {active && (
            <div className="p-5 rounded-2xl border border-white/10 bg-surface/60" data-testid="player-meta">
              <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-gold/80 mb-2">
                <Radio className="w-3 h-3" /> Şu An
              </div>
              <h2 className="font-display font-extrabold text-3xl tracking-tight">{active.name}</h2>
              <div className="mt-1 text-sm text-white/55">
                {active.category}
              </div>
            </div>
          )}
        </section>
      </main>

      <Footer />
    </div>
  );
}

function Pill({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-2.5 h-7 rounded-full text-[11px] font-semibold tracking-tight transition ${
        active
          ? "bg-gold text-black"
          : "bg-white/5 text-white/60 hover:bg-white/10 hover:text-white"
      }`}
    >
      {children}
    </button>
  );
}
